import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  LEGAL_DOCUMENT_TYPES,
  LegalDocument,
  LegalDocumentType,
  getLegalDocumentHistory,
} from "../../services/legalDocument.service";
import { formatVersionDisplay } from "../../utils/semver";
import {
  ShieldCheck,
  FileText,
  Eye,
  Pencil,
  History,
  Check,
  X,
  ScrollText,
  ArrowLeft,
  Maximize2,
  Minimize2,
  Clock,
  User,
  AlertCircle,
} from "lucide-react";

interface TypeRow {
  type: LegalDocumentType;
  label: string;
  versions: LegalDocument[];
  published: LegalDocument | null;
  latest: LegalDocument | null;
}

const buildRow = (typeRow: { value: LegalDocumentType; label: string }, versions: LegalDocument[]): TypeRow => {
  return {
    type: typeRow.value,
    label: typeRow.label,
    versions,
    published: versions.find((v) => v.status === "PUBLISHED") ?? null,
    latest: versions.length > 0 ? versions[versions.length - 1] : null,
  };
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const stripHtml = (html: string): string =>
  (html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

const LegalContentList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [histories, setHistories] = useState<Partial<Record<LegalDocumentType, LegalDocument[]>>>({});
  const [loading, setLoading] = useState(true);
  const [isMaximized, setIsMaximized] = useState(false);

  // URL search params state
  const mode = searchParams.get("mode"); // "view" | "history" | null
  const currentType = searchParams.get("type") as LegalDocumentType | null;
  const currentDocId = searchParams.get("id");
  const fromHistory = searchParams.get("from") === "history";

  // Escape key listener for maximized fullscreen mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isMaximized) {
        setIsMaximized(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMaximized]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const entries = await Promise.all(
        LEGAL_DOCUMENT_TYPES.map(async (t) => [t.value, await getLegalDocumentHistory(t.value)] as const)
      );
      setHistories(Object.fromEntries(entries));
    } catch {
      toast.error("Failed to load legal documents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const rows = useMemo(
    () =>
      LEGAL_DOCUMENT_TYPES.map((t) => buildRow(t, histories[t.value] || [])),
    [histories]
  );

  const rowForType = useCallback(
    (type: LegalDocumentType): TypeRow | undefined => rows.find((r) => r.type === type),
    [rows]
  );

  // --- View action ---
  const openView = (type: LegalDocumentType) => {
    const row = rowForType(type);
    if (!row) return;
    const doc = row.published ?? row.latest;
    if (doc) {
      setSearchParams({ mode: "view", type, id: doc.id });
    } else {
      toast("No version created yet for " + row.label + ". Click Edit to create the first draft.");
    }
  };

  // --- Edit action ---
  const openEdit = (type: LegalDocumentType) => {
    navigate(`/legal-content/edit?documentType=${type}`);
  };

  // --- History action ---
  const openHistory = (type: LegalDocumentType) => {
    setSearchParams({ mode: "history", type });
  };

  // Resolve currently viewed document when mode === "view"
  const activeViewDoc = useMemo(() => {
    if (mode !== "view" || !currentType) return null;
    const list = histories[currentType] || [];
    if (currentDocId) {
      const match = list.find((d) => d.id === currentDocId);
      if (match) return match;
    }
    // Fallback: published or latest version
    return list.find((d) => d.status === "PUBLISHED") ?? (list.length > 0 ? list[list.length - 1] : null);
  }, [mode, currentType, currentDocId, histories]);

  const activeHistoryRow = useMemo(() => {
    if (mode !== "history" || !currentType) return null;
    return rowForType(currentType);
  }, [mode, currentType, rowForType]);

  // Back handlers
  const handleBackFromView = () => {
    setIsMaximized(false);
    if (fromHistory && currentType) {
      setSearchParams({ mode: "history", type: currentType });
    } else {
      setSearchParams({});
    }
  };

  const handleBackFromHistory = () => {
    setSearchParams({});
  };

  // =========================================================================
  // VIEW MODE: Full-Screen Document View
  // =========================================================================
  if (mode === "view") {
    const currentTypeLabel = LEGAL_DOCUMENT_TYPES.find((t) => t.value === currentType)?.label || currentType || "Legal Document";

    return (
      <div
        className={
          isMaximized
            ? "fixed inset-0 z-50 bg-gray-50 overflow-y-auto p-4 sm:p-6 lg:p-8"
            : "space-y-6"
        }
      >
        {/* Top Header & Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3">
            <button
              onClick={handleBackFromView}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors shrink-0 mt-0.5 sm:mt-0"
              title={fromHistory ? "Back to version history" : "Back to legal content list"}
              aria-label="Back"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-md bg-gray-100 text-gray-700 border border-gray-200">
                  {currentTypeLabel}
                </span>
                {activeViewDoc && (
                  <>
                    <span className="text-xs font-semibold px-2.5 py-0.5 rounded-md bg-primary-50 text-primary-700 border border-primary-100">
                      v{formatVersionDisplay(activeViewDoc.version)}
                    </span>
                    <StatusBadge status={activeViewDoc.status} />
                  </>
                )}
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight truncate">
                {activeViewDoc?.title || currentTypeLabel}
              </h1>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3 self-end md:self-center">
            {/* Fullscreen maximize / minimize button */}
            <button
              onClick={() => setIsMaximized(!isMaximized)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg shadow-sm transition-colors"
              title={isMaximized ? "Exit full screen (Esc)" : "Expand to full screen"}
            >
              {isMaximized ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
              <span>{isMaximized ? "Exit Fullscreen" : "Fullscreen"}</span>
            </button>

            {/* Version History shortcut button */}
            {currentType && (
              <button
                onClick={() => setSearchParams({ mode: "history", type: currentType })}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-primary-600 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-lg transition-colors"
              >
                <History size={15} />
                <span>Version History</span>
              </button>
            )}

            {/* Edit Draft button */}
            {currentType && (
              <button
                onClick={() => openEdit(currentType)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-sm transition-colors"
              >
                <Pencil size={15} />
                <span>Edit Draft</span>
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4" />
            <p className="text-gray-500 animate-pulse">Loading legal document...</p>
          </div>
        ) : !activeViewDoc ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <AlertCircle size={40} className="mx-auto text-amber-500 mb-3" />
            <h2 className="text-base font-semibold text-gray-900">No Document Content Found</h2>
            <p className="text-sm text-gray-500 mt-1 mb-6">
              No version has been created or published for this document type yet.
            </p>
            {currentType && (
              <button
                onClick={() => openEdit(currentType)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 transition-colors"
              >
                <Pencil size={16} />
                <span>Create First Draft</span>
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Metadata Summary Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 sm:p-6">
              <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">
                Document Details & Status
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <MetaItem label="Status" value={<StatusBadge status={activeViewDoc.status} />} />
                <MetaItem
                  label="Version"
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      <span>v{formatVersionDisplay(activeViewDoc.version)}</span>
                      {activeViewDoc.changeType && (
                        <span className="px-1.5 py-0.2 rounded text-[10px] font-semibold bg-gray-100 text-gray-700 border border-gray-200 uppercase">
                          {activeViewDoc.changeType}
                        </span>
                      )}
                    </span>
                  }
                />
                <MetaItem
                  label="Published Date"
                  value={formatDate(activeViewDoc.publishedAt) || "Not published"}
                />
                <MetaItem
                  label="Effective Date"
                  value={formatDate(activeViewDoc.effectiveFrom) || "Immediately"}
                />
                <MetaItem
                  label="Last Updated"
                  value={formatDate(activeViewDoc.updatedAt) || "—"}
                />
                <MetaItem
                  label="Re-acceptance"
                  value={
                    activeViewDoc.requiresReAcceptance ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-xs">
                        <Check size={14} /> Required
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-gray-400 font-medium text-xs">
                        <X size={14} /> Not required
                      </span>
                    )
                  }
                />
              </div>

              {(activeViewDoc.createdByName || activeViewDoc.updatedByName) && (
                <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-6 text-xs text-gray-500">
                  {activeViewDoc.createdByName && (
                    <div className="flex items-center gap-1.5">
                      <User size={14} className="text-gray-400" />
                      <span>Created by: <strong className="text-gray-700 font-medium">{activeViewDoc.createdByName}</strong></span>
                    </div>
                  )}
                  {activeViewDoc.updatedByName && (
                    <div className="flex items-center gap-1.5">
                      <Clock size={14} className="text-gray-400" />
                      <span>Last edited by: <strong className="text-gray-700 font-medium">{activeViewDoc.updatedByName}</strong></span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Document Content Reader */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Branded Header Banner */}
              <div className="bg-gradient-to-r from-neutral-50 to-gray-50 border-b border-gray-200 px-6 sm:px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3.5">
                    <div className="h-10 w-10 rounded-lg bg-primary-600 flex items-center justify-center text-white font-bold text-lg shadow-sm">
                      D
                    </div>
                    <div>
                      <p className="text-base font-bold text-gray-900 leading-tight">DiagnomIQ</p>
                      <p className="text-xs font-medium text-primary-700 mt-0.5">
                        Official Legal Document · {currentTypeLabel}
                      </p>
                    </div>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-gray-400">Version</p>
                    <p className="text-sm font-semibold text-gray-700">v{formatVersionDisplay(activeViewDoc.version)}</p>
                  </div>
                </div>
              </div>

              {/* Formatted Legal Document Text */}
              <div className="p-6 sm:p-10 lg:p-12">
                <div className="mb-6 pb-6 border-b border-gray-100">
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                    {activeViewDoc.title}
                  </h2>
                  <p className="text-sm text-gray-500 mt-2">
                    {activeViewDoc.effectiveFrom
                      ? `Effective as of ${formatDate(activeViewDoc.effectiveFrom)}`
                      : activeViewDoc.publishedAt
                      ? `Published on ${formatDate(activeViewDoc.publishedAt)}`
                      : "Draft version"}
                  </p>
                </div>

                {stripHtml(activeViewDoc.content) ? (
                  <div
                    className="text-base text-gray-800 leading-relaxed max-w-none [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-8 [&_h1]:mb-3 [&_h1]:text-gray-900 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:text-gray-900 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-5 [&_h3]:mb-1 [&_h3]:text-gray-800 [&_p]:my-3.5 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3.5 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-3.5 [&_li]:mb-1.5 [&_a]:text-primary-600 [&_a]:underline hover:[&_a]:text-primary-700 [&_strong]:font-semibold [&_blockquote]:border-l-4 [&_blockquote]:border-primary-300 [&_blockquote]:bg-primary-50/40 [&_blockquote]:py-2 [&_blockquote]:px-4 [&_blockquote]:my-4 [&_blockquote]:rounded-r [&_blockquote]:text-gray-700 [&_hr]:my-8 [&_hr]:border-gray-200"
                    dangerouslySetInnerHTML={{ __html: activeViewDoc.content }}
                  />
                ) : (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/50 p-12 text-center">
                    <ScrollText size={36} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-sm font-medium text-gray-600">This document has no content yet.</p>
                    <p className="text-xs text-gray-400 mt-1 mb-4">Click below to add content in the editor.</p>
                    {currentType && (
                      <button
                        onClick={() => openEdit(currentType)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                      >
                        <Pencil size={14} />
                        <span>Edit Content</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Document Reader Footer */}
              <div className="bg-gray-50 border-t border-gray-200 px-6 sm:px-10 py-4 flex items-center justify-between">
                <button
                  onClick={handleBackFromView}
                  className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <ArrowLeft size={14} />
                  <span>{fromHistory ? "Back to History" : "Back to Legal Documents"}</span>
                </button>
                {currentType && (
                  <button
                    onClick={() => openEdit(currentType)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    <Pencil size={14} />
                    <span>Edit this document</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // =========================================================================
  // HISTORY MODE: Full-Screen Version History View
  // =========================================================================
  if (mode === "history") {
    const currentTypeLabel = LEGAL_DOCUMENT_TYPES.find((t) => t.value === currentType)?.label || currentType || "Legal Document";
    const historyVersions = [...(activeHistoryRow?.versions || [])].reverse();
    const publishedVersion = activeHistoryRow?.published;
    const latestDraft = activeHistoryRow?.versions.find((v) => v.status === "DRAFT");

    return (
      <div className="space-y-6">
        {/* History Header & Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3">
            <button
              onClick={handleBackFromHistory}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors shrink-0 mt-0.5 sm:mt-0"
              title="Back to legal content list"
              aria-label="Back"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-md bg-primary-50 text-primary-700 border border-primary-100">
                  {currentTypeLabel}
                </span>
                <span className="text-xs text-gray-400 font-medium">
                  {historyVersions.length} {historyVersions.length === 1 ? "version" : "versions"}
                </span>
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                Version History — {currentTypeLabel}
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                Historical versions are read-only. To modify or publish updates, create a new version from the Edit action.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 self-end sm:self-center">
            {currentType && (
              <button
                onClick={() => openEdit(currentType)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-sm transition-colors"
              >
                <Pencil size={15} />
                <span>Edit / New Draft</span>
              </button>
            )}
          </div>
        </div>

        {/* Overview KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold shrink-0">
              <Check size={20} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Active Published</p>
              <p className="text-sm font-semibold text-gray-900 mt-0.5 truncate">
                {publishedVersion ? `Version ${formatVersionDisplay(publishedVersion.version)}` : "None published"}
              </p>
              <p className="text-xs text-gray-400 truncate">
                {publishedVersion?.publishedAt ? `Published ${formatDate(publishedVersion.publishedAt)}` : "—"}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold shrink-0">
              <Pencil size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Current Draft</p>
              <p className="text-sm font-semibold text-gray-900 mt-0.5 truncate">
                {latestDraft ? `Version ${formatVersionDisplay(latestDraft.version)}` : "No active draft"}
              </p>
              <p className="text-xs text-gray-400 truncate">
                {latestDraft?.updatedAt ? `Updated ${formatDate(latestDraft.updatedAt)}` : "—"}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center font-bold shrink-0">
              <History size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Total Recorded</p>
              <p className="text-sm font-semibold text-gray-900 mt-0.5">
                {historyVersions.length} {historyVersions.length === 1 ? "Version" : "Versions"}
              </p>
              <p className="text-xs text-gray-400">Complete audit trail</p>
            </div>
          </div>
        </div>

        {/* Full-Screen Versions Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4" />
              <p className="text-gray-500 animate-pulse">Loading version history...</p>
            </div>
          ) : historyVersions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50/80 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700">Version</th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700">Status</th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700">Title</th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700">Author / Editor</th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700">Created Date</th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700">Published Date</th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700">Updated Date</th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700">Re-acceptance</th>
                    <th className="px-6 py-3.5 text-xs font-bold text-gray-700 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {historyVersions.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50/80 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold bg-neutral-100 text-neutral-800 border border-neutral-200">
                            v{formatVersionDisplay(doc.version)}
                          </span>
                          {doc.changeType && (
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                                doc.changeType === "MAJOR"
                                  ? "bg-purple-100 text-purple-700 border border-purple-200"
                                  : doc.changeType === "MINOR"
                                  ? "bg-blue-100 text-blue-700 border border-blue-200"
                                  : "bg-gray-100 text-gray-700 border border-gray-200"
                              }`}
                            >
                              {doc.changeType}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={doc.status} />
                      </td>
                      <td className="px-6 py-4 text-gray-900 font-medium max-w-xs truncate">
                        {doc.title || currentTypeLabel}
                      </td>
                      <td className="px-6 py-4 text-gray-600 whitespace-nowrap text-xs">
                        {doc.createdByName
                          ? `${doc.createdByName}${doc.updatedByName && doc.updatedByName !== doc.createdByName ? ` → ${doc.updatedByName}` : ""}`
                          : doc.createdBy
                          ? `${doc.createdBy.slice(0, 8)}…`
                          : "—"}
                      </td>
                      <td className="px-6 py-4 text-gray-600 whitespace-nowrap text-xs">
                        {formatDate(doc.createdAt) || "—"}
                      </td>
                      <td className="px-6 py-4 text-gray-600 whitespace-nowrap text-xs">
                        {formatDate(doc.publishedAt) || "—"}
                      </td>
                      <td className="px-6 py-4 text-gray-600 whitespace-nowrap text-xs">
                        {formatDate(doc.updatedAt) || "—"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {doc.requiresReAcceptance ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                            <Check size={14} />
                            Required
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
                            <X size={14} />
                            Not required
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() =>
                              setSearchParams({
                                mode: "view",
                                type: currentType!,
                                id: doc.id,
                                from: "history",
                              })
                            }
                            title="View Document"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:bg-primary-50 hover:text-primary-700 rounded-lg px-2.5 py-1.5 transition-colors"
                          >
                            <Eye size={15} />
                            <span>View</span>
                          </button>

                          {doc.status === "DRAFT" && (
                            <button
                              onClick={() => openEdit(currentType!)}
                              title="Edit Draft"
                              className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-lg px-2.5 py-1.5 transition-colors"
                            >
                              <Pencil size={15} />
                              <span>Edit</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <FileText size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-600 text-base font-medium">No versions created yet for {currentTypeLabel}.</p>
              <p className="text-gray-400 text-sm mt-1 mb-5">Create the first version to get started.</p>
              {currentType && (
                <button
                  onClick={() => openEdit(currentType)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 transition-colors"
                >
                  <Pencil size={16} />
                  <span>Create First Draft</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // =========================================================================
  // DEFAULT: Legal Content Types List View
  // =========================================================================
  const hasAnyDocuments = rows.some((r) => r.versions.length > 0);

  return (
    <div className="space-y-6">
      {/* Intro card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600 shrink-0">
          <ShieldCheck size={20} />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">Legal Content Management</p>
          <p className="text-sm text-gray-500 mt-0.5">
            Draft, publish and version the legal documents that users must accept. Only one version of
            each document can be published at a time; publishing a new version automatically archives
            the previous one.
          </p>
        </div>
      </div>

      {/* Documents table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-500 animate-pulse">Loading legal documents...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-50/80 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3.5 text-xs font-bold text-gray-700">Document</th>
                  <th className="px-6 py-3.5 text-xs font-bold text-gray-700">Published Version</th>
                  <th className="px-6 py-3.5 text-xs font-bold text-gray-700">Status</th>
                  <th className="hidden md:table-cell px-6 py-3.5 text-xs font-bold text-gray-700">
                    Last Updated
                  </th>
                  <th className="hidden lg:table-cell px-6 py-3.5 text-xs font-bold text-gray-700">
                    Effective Date
                  </th>
                  <th className="hidden lg:table-cell px-6 py-3.5 text-xs font-bold text-gray-700">
                    Re-acceptance
                  </th>
                  <th className="px-6 py-3.5 text-xs font-bold text-gray-700 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {rows.map((row) => (
                  <tr key={row.type} className="hover:bg-gray-50/80 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600 shrink-0">
                          <FileText size={18} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">
                            {row.latest ? row.latest.title : row.label}
                          </p>
                          <p className="text-xs text-gray-400">{row.label}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {row.published ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                          v{formatVersionDisplay(row.published.version)}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
                          Not Published
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {row.latest ? (
                        <StatusBadge status={row.latest.status} />
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                          Not created
                        </span>
                      )}
                    </td>
                    <td className="hidden md:table-cell px-6 py-4 text-gray-600 whitespace-nowrap">
                      {row.latest ? formatDate(row.latest.updatedAt) : "—"}
                    </td>
                    <td className="hidden lg:table-cell px-6 py-4 text-gray-600 whitespace-nowrap">
                      {formatDate((row.published ?? row.latest)?.effectiveFrom) || "—"}
                    </td>
                    <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap">
                      {row.latest ? (
                        row.latest.requiresReAcceptance ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                            <Check size={14} />
                            Required
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
                            <X size={14} />
                            Not required
                          </span>
                        )
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <ActionButton
                          icon={<Eye size={15} />}
                          label="View"
                          onClick={() => openView(row.type)}
                        />
                        <ActionButton
                          icon={<Pencil size={15} />}
                          label="Edit"
                          onClick={() => openEdit(row.type)}
                        />
                        <ActionButton
                          icon={<History size={15} />}
                          label="History"
                          onClick={() => openHistory(row.type)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !hasAnyDocuments && (
          <div className="border-t border-gray-100 p-10 text-center">
            <FileText size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-base font-medium">No legal documents created yet</p>
            <p className="text-gray-400 text-sm mt-1">
              Use the Edit action on any document above to create its first draft.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

/* --- Helper Components --- */

const STATUS_LABELS: Record<LegalDocument["status"], string> = {
  DRAFT: "Draft",
  PUBLISHED: "Published",
  ARCHIVED: "Archived",
};

const StatusBadge = ({ status }: { status: LegalDocument["status"] }) => {
  let classes = "bg-gray-100 text-gray-700 border-gray-200";
  if (status === "PUBLISHED") {
    classes = "bg-emerald-50 text-emerald-700 border-emerald-200";
  } else if (status === "DRAFT") {
    classes = "bg-blue-50 text-blue-700 border-blue-200";
  } else if (status === "ARCHIVED") {
    classes = "bg-gray-100 text-gray-500 border-gray-200";
  }
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${classes}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
};

const MetaItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</p>
    <div className="text-sm font-medium text-gray-900">{value}</div>
  </div>
);

const ActionButton = ({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    title={label}
    className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:bg-primary-50 hover:text-primary-700 rounded-lg px-2.5 py-1.5 transition-colors"
  >
    {icon}
    <span>{label}</span>
  </button>
);

export default LegalContentList;